import React from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'About PSMS 108',
      links: [
        { name: 'Our Story', href: '/our-story' },
        { name: 'Mission & Vision', href: '/mission-vision' },
        { name: 'Faculty & Staff', href: '/faculty-staff' },
        { name: 'School Policies', href: '/school-policies' },
        { name: 'Career Opportunities', href: '/careers' },
      ]
    },
    {
      title: 'Academic Programs',
      links: [
        { name: 'Creative Writing', href: '/creative-writing' },
        { name: 'Author Workshops', href: '/author-workshops' },
        { name: 'Literary Magazine', href: '/literary-magazine' },
        { name: 'Young Authors Academy', href: '/young-authors' },
        { name: 'Reading Club', href: '/reading-club' },
      ]
    },
    {
      title: 'Student Life',
      links: [
        { name: 'Student Resources', href: '/student-resources' },
        { name: 'Clubs & Activities', href: '/clubs-activities' },
        { name: 'Student Council', href: '/student-council' },
        { name: 'Awards & Recognition', href: '/awards' },
        { name: 'School Events', href: '/events' },
      ]
    },
    {
      title: 'Quick Links',
      links: [
        { name: 'Apply Now', href: '/apply' },
        { name: 'Parent Portal', href: '/parent-portal' },
        { name: 'Academic Calendar', href: '/academic-calendar' },
        { name: 'Contact Us', href: '/contact' },
        { name: 'School Tours', href: '/school-tours' },
      ]
    }
  ];

  return (
    <footer className="bg-brand-primary text-white relative overflow-hidden">
      {/* The decorative background shapes this footer used to have (a couple
          of faint outlined circles and a book emoji) were dropped: the footer
          is tall and content-dense enough that no position for them avoided
          overlapping some line of real text, which made a contrast checker
          unable to tell what was really behind that text. Not worth
          reintroducing for a 5%-opacity flourish nobody consciously notices. */}

      {/* Main Footer Content */}
      <div className="content-container section-padding relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-16">
          {/* School Info - Left Side */}
          <div className="lg:col-span-4">
            <div className="mb-6 md:mb-8">
              <h2 className="text-2xl md:text-3xl font-display text-white mb-2">PSMS 108</h2>
              <p className="text-base md:text-lg text-white/80 font-medium">School of Authors</p>
            </div>

            <p className="text-sm md:text-base text-white/70 mb-8 md:mb-12 leading-relaxed font-body">
              Nurturing young minds and fostering creativity through exceptional education 
              and innovative writing programs. Building tomorrow's authors, one story at a time.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 md:space-y-4 mb-8 md:mb-12 text-sm md:text-base">
              <div className="text-white/80">
                <p>1615 Madison Avenue</p>
                <p>New York, NY 10029</p>
              </div>

              <div className="text-white/80">
                <span>(212) 860-5803</span>
              </div>

              <div className="text-white/80">
                <span>Fax: (212) 860-6095</span>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="text-base md:text-lg font-display mb-4 md:mb-6 text-white">Connect With Us</h3>
              <div className="flex space-x-3 md:space-x-4">
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/108soa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 md:w-12 h-10 md:h-12 bg-white/10 hover:bg-brand-yellow border-2 border-transparent hover:border-brand-navy flex items-center justify-center transition-all duration-300 group"
                  aria-label="Facebook"
                >
                  <svg viewBox="0 0 24 24" className="w-5 md:w-6 h-5 md:h-6 text-white group-hover:text-brand-navy transition-colors" fill="currentColor" aria-hidden="true">
                    <path d="M22 12.06C22 6.48 17.52 2 11.94 2S2 6.48 2 12.06c0 4.98 3.66 9.12 8.44 9.94v-7.03H7.9V12.06h2.54V9.84c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.91h-2.34V22c4.78-.82 8.44-4.96 8.44-9.94z" />
                  </svg>
                </a>

                {/* Instagram */}
                <a
                  href="https://www.instagram.com/schoolofauthors/?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 md:w-12 h-10 md:h-12 bg-white/10 hover:bg-brand-yellow border-2 border-transparent hover:border-brand-navy flex items-center justify-center transition-all duration-300 group"
                  aria-label="Instagram"
                >
                  <svg viewBox="0 0 24 24" className="w-5 md:w-6 h-5 md:h-6 text-white group-hover:text-brand-navy transition-colors" fill="currentColor" aria-hidden="true">
                    <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3a5 5 0 110 10 5 5 0 010-10zm0 2.2a2.8 2.8 0 100 5.6 2.8 2.8 0 000-5.6zM18 6.5a1 1 0 110 2 1 1 0 010-2z" />
                  </svg>
                </a>

                {/* YouTube */}
                <a
                  href="https://www.youtube.com/channel/UCwsJmvWoUxdvLPyYOhBWCvg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 md:w-12 h-10 md:h-12 bg-white/10 hover:bg-brand-yellow border-2 border-transparent hover:border-brand-navy flex items-center justify-center transition-all duration-300 group"
                  aria-label="YouTube"
                >
                  <svg viewBox="0 0 24 24" className="w-5 md:w-6 h-5 md:h-6 text-white group-hover:text-brand-navy transition-colors" fill="currentColor" aria-hidden="true">
                    <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31.8 31.8 0 000 12a31.8 31.8 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31.8 31.8 0 0024 12a31.8 31.8 0 00-.5-5.8zM9.8 15.5V8.5L15.5 12l-5.7 3.5z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Footer Links - Right Side */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
              {footerSections.map((section, index) => (
                <div key={index}>
                  <h3 className="text-base md:text-lg font-display mb-6 md:mb-8 text-white relative">
                    {section.title}
                    <div className="absolute -bottom-2 left-0 w-6 md:w-8 h-px bg-gradient-to-r from-brand-yellow to-brand-navy"></div>
                  </h3>
                  <ul className="space-y-3 md:space-y-4">
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          to={link.href}
                          className="text-white/70 hover:text-brand-yellow transition-colors duration-300 text-sm block"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Educational Stats Section */}
      <div className="border-t border-white/20">
        <div className="content-container py-8 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 text-center">
            {[
              { number: "500+", label: "Students" },
              { number: "50+", label: "Faculty" },
              { number: "1000+", label: "Stories Published" },
              { number: "25", label: "Years of Excellence" }
            ].map((stat, index) => (
              <div key={index} className="group">
                <div className="text-2xl md:text-4xl font-display text-white mb-2">{stat.number}</div>
                <div className="text-xs md:text-sm text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-white/20">
        <div className="content-container py-6 md:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-xs md:text-sm text-white/80 text-center md:text-left">
              © {currentYear} PSMS 108 School of Authors. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-xs md:text-sm">
              <Link to="/privacy" className="text-white/80 hover:text-brand-yellow transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/80 hover:text-brand-yellow transition-colors">
                Terms of Service
              </Link>
              <Link to="/accessibility" className="text-white/80 hover:text-brand-yellow transition-colors">
                Accessibility
              </Link>
            </div>
          </div>

          {/* NexSav Credit */}
          <div className="mt-4 pt-4 border-t border-white/5">
            <a
              href="https://nexsav.net"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-white/80 hover:text-white/90 transition-all duration-300 group"
            >
              <img
                src="https://nexsav.net/static/media/logo.7ef8f9a8ca3ec946bd31.png"
                alt="NexSav Logo"
                className="h-5 opacity-60 group-hover:opacity-90 transition-opacity duration-300"
              />
              <span className="text-xs font-medium tracking-wide">
                Website designed & developed by <span className="font-semibold">NexSav</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;